import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useToast } from "../mesageToast/ToastContainer";
import { useQueryClient } from "@tanstack/react-query";
import api from "../services/api";

const getWsUrl = () => {
  const baseUrl = api.defaults.baseURL || "https://api.eatitgo.com/mobo";
  try {
    const urlObj = new URL(baseUrl);
    return `${urlObj.protocol}//${urlObj.host}/ws/support`;
  } catch (e) {
    // If it's a relative URL like "/mobo"
    return `${window.location.protocol}//${window.location.host}/ws/support`;
  }
};

const WS_URL = getWsUrl();

export let globalSupportSocket = null;

export const disconnectSupportSocket = () => {
  if (globalSupportSocket) {
    globalSupportSocket.disconnect();
    globalSupportSocket = null;
  }
};

const useSupportWebSocket = (role) => {
  const socketRef = useRef(null);
  const addToast = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    let rawToken =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    let tokenValue = rawToken;

    if (rawToken) {
      try {
        const parsed = JSON.parse(rawToken);
        if (parsed.token) {
          tokenValue = parsed.token;
        } else if (parsed.restaurant) {
          tokenValue = parsed.restaurant;
        }
      } catch (e) {
        // rawToken is a plain string
      }
    }

    if (!tokenValue) {
      return;
    }

    socketRef.current = io(WS_URL, {
      path: "/updates",
      auth: { token: tokenValue },
      transports: ["websocket", "polling"],
    });

    globalSupportSocket = socketRef.current;

    socketRef.current.on("connect", () => {
      console.log("Connected to support websocket namespace");
    });

    socketRef.current.on("new_ticket_assigned", (data) => {
      if (role === "mid-office") {
        if (addToast)
          addToast(
            "New Ticket",
            `Ticket #${data.ticketId} assigned to you.`,
            "info",
          );
        queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      }
    });

    socketRef.current.on("ticket_escalated_backoffice", (data) => {
      if (role === "back-office") {
        if (addToast)
          addToast(
            "Ticket Escalated",
            `Ticket #${data.ticketId} escalated from Mid-Office.`,
            "warning",
          );
        queryClient.invalidateQueries({
          queryKey: ["support-escalations", "unassigned"],
        });
      }
    });

    socketRef.current.on("ticket_assigned", (data) => {
      if (role === "back-office") {
        if (addToast)
          addToast(
            "Success",
            `Ticket #${data.ticketId} assigned to you.`,
            "success",
          );
        queryClient.invalidateQueries({
          queryKey: ["support-escalations", "my-tickets"],
        });
      }
    });

    socketRef.current.on("customer_message", (data) => {
      console.log("Received customer_message:", data);
      if (addToast)
        addToast(
          "New Message",
          `${data.senderName || "Customer"} sent a message on ticket #${data.ticketId}.`,
          "success",
        );
      // Refresh the ticket detail view if it's open
      queryClient.invalidateQueries({
        queryKey: ["support-ticket", data.ticketId],
      });
      // Also refresh the ticket list
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support-escalations"] });
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (globalSupportSocket === socketRef.current) {
        globalSupportSocket = null;
      }
    };
  }, [role, addToast, queryClient]);

  return socketRef.current;
};

export default useSupportWebSocket;
